<template>
  <div
    class="flex items-center gap-2 overflow-hidden my-2"
    :class="{ 'flex-wrap': isExpanded }"
  >
    <template v-if="hasUtmParameterSets">
      <div
        v-for="(formattedSet, index) in displayedSets"
        :key="index"
        v-tooltipable
        class="min-w-0 truncate rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs"
      >
        {{ formattedSet }}
      </div>

      <button
        v-if="hasOverflow"
        class="shrink-0 cursor-pointer rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs hover:bg-gray-100"
        @click="isExpanded = !isExpanded"
      >
        {{ buttonText }}
      </button>
    </template>

    <template v-else>
      <badge-list
        :value="utmParameters"
        badge-class="h-5 max-w-56 truncate text-xxs leading-4 font-semibold uppercase"
        pill-class="h-5"
        :show-num-of-items="2"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { formatUtmSet } from "@/helpers/tracking";

import BadgeList from "@/components/ui/BadgeList.vue";

import type { UtmParameter, UtmParameterSet } from "@/models/applications";

interface Props {
  modelValue?: UtmParameter[];
  model?: {
    utm_parameter_sets?: UtmParameterSet[];
  };
}

const { modelValue, model } = defineProps<Props>();

const { t } = useI18n();

const VISIBLE_COUNT = 1;

const isExpanded = ref(false);

const buttonText = computed(() =>
  isExpanded.value ? t("COMMON.HIDE") : `+${hiddenCount.value}`
);

const utmParameterSetsData = computed<UtmParameterSet[]>(
  () => model?.utm_parameter_sets ?? []
);

const hasUtmParameterSets = computed(() => !!utmParameterSetsData.value.length);

const formattedSets = computed(() =>
  utmParameterSetsData.value.map(formatUtmSet)
);

const displayedSets = computed(() => {
  if (isExpanded.value) {
    return formattedSets.value;
  }
  return formattedSets.value.slice(0, VISIBLE_COUNT);
});

const hasOverflow = computed(() => formattedSets.value.length > VISIBLE_COUNT);

const hiddenCount = computed(() => formattedSets.value.length - VISIBLE_COUNT);

// Legacy format for backwards compatibility
const utmParameters = computed(() =>
  modelValue?.map((param) => `${param.type}: ${param.value}`)
);
</script>
